import React, { useState, useEffect, useCallback } from 'react';
import { useApp, ROLE_LABELS } from '../context/AppContext';
import { InstructionGuide } from '../components/layout/InstructionGuide';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Users, MapPin, Store, Calendar, TrendingUp, TrendingDown,
  Target, DollarSign, ArrowUpRight, ArrowDownRight, CheckCircle2, Clock, FileDown
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const CHART_COLORS = ['#D97706', '#F59E0B', '#FBBF24', '#1E293B', '#64748B'];

export default function Dashboard() {
  const { api, currentUser, userZone, roleLevel, selectedBusiness, businessLines } = useApp();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [recentVisits, setRecentVisits] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const statsParams = {};
      const analyticsParams = {};
      if (currentUser) {
        statsParams.user_id = currentUser.id;
        statsParams.role = currentUser.role;
        if (userZone) {
          statsParams.zone = userZone;
          analyticsParams.zone = userZone;
        }
      }
      if (selectedBusiness) {
        statsParams.business_id = selectedBusiness;
        analyticsParams.business_id = selectedBusiness;
      }
      const visitParams = {};
      if (selectedBusiness) visitParams.business_id = selectedBusiness;
      if (roleLevel >= 6) visitParams.user_id = currentUser?.id;
      else if (userZone) visitParams.zone = userZone;

      const [statsRes, analyticsRes, visitsRes] = await Promise.all([
        api.getDashboardStats(statsParams),
        api.getPerformanceAnalytics(analyticsParams),
        api.getVisits(visitParams),
      ]);
      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
      setRecentVisits(visitsRes.data.slice(0, 5));
    } catch (e) {
      console.error(e);
    }
  }, [api, currentUser, userZone, roleLevel, selectedBusiness]);

  useEffect(() => { loadData(); }, [loadData]);

  const activeBiz = businessLines.find(b => b.id === selectedBusiness);

  if (!stats) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-slate-400">Loading dashboard...</div></div>;

  const targetPercent = stats.total_target > 0 ? Math.round((stats.total_revenue / stats.total_target) * 100) : 0;
  const typeData = analytics?.types?.map(t => ({ name: t.type === 'business' ? 'Business' : 'Individual', value: t.revenue })) || [];

  return (
    <div data-testid="dashboard-page" className="space-y-8 fade-in">
      {/* Welcome */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900">
            Welcome back, {currentUser?.name?.split(' ')[0] || 'User'}
          </h1>
          <p className="text-base text-slate-500 mt-1">
            {activeBiz ? `${activeBiz.label} ` : ''}{userZone ? `${userZone} zone overview` : 'Overall sales performance overview'}
          </p>
        </div>
        <a href={api.exportPerformance(userZone)} download data-testid="export-dashboard-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
          <FileDown className="w-4 h-4" /> Export Report
        </a>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard
          testId="kpi-revenue"
          icon={DollarSign}
          label="Total Revenue"
          value={`${(stats.total_revenue / 100000).toFixed(1)}L`}
          sub={`Target: ${(stats.total_target / 100000).toFixed(1)}L`}
          trend={stats.growth_percent}
          color="amber"
        />
        <KPICard
          testId="kpi-visits"
          icon={Calendar}
          label="Total Visits"
          value={stats.total_visits}
          sub={`${stats.completed_visits} completed`}
          trend={stats.conversion_rate}
          trendLabel="completion"
          color="blue"
        />
        <KPICard
          testId="kpi-sales-points"
          icon={Store}
          label="Sales Points"
          value={stats.total_sales_points}
          sub={`${stats.sp_business} business, ${stats.sp_individual} individual`}
          color="emerald"
        />
        <KPICard
          testId="kpi-team"
          icon={Users}
          label="Team Members"
          value={stats.active_users}
          sub={`${stats.total_users} total`}
          color="purple"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Revenue by Zone */}
        <Card className="lg:col-span-8 border border-border/60 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-bold text-slate-900">Revenue by Zone</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics?.zones || []} barSize={32}>
                  <XAxis dataKey="zone" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748B' }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94A3B8' }} tickFormatter={v => `${(v/100000).toFixed(0)}L`} />
                  <Tooltip formatter={(v) => `${(v/1000).toFixed(0)}K`} />
                  <Bar dataKey="revenue" fill="#D97706" radius={[6, 6, 0, 0]} name="Revenue" />
                  <Bar dataKey="target" fill="#FDE68A" radius={[6, 6, 0, 0]} name="Target" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Revenue by Type - Pie */}
        <Card className="lg:col-span-4 border border-border/60 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-bold text-slate-900">Revenue Split</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={typeData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={40}>
                    {typeData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `${(v/1000).toFixed(0)}K`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-4 mt-2">
              {typeData.map((t, i) => (
                <div key={t.name} className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded" style={{ background: CHART_COLORS[i] }} />
                  <span className="text-slate-600">{t.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row: Target Progress + Recent Visits */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Target Progress */}
        <Card className="lg:col-span-4 border border-border/60 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Target className="w-4 h-4 text-amber-600" /> Target Progress
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center">
              <div className="relative inline-flex items-center justify-center w-32 h-32">
                <svg className="w-32 h-32 -rotate-90">
                  <circle cx="64" cy="64" r="56" stroke="#F1F5F9" strokeWidth="8" fill="none" />
                  <circle cx="64" cy="64" r="56" stroke="#D97706" strokeWidth="8" fill="none"
                    strokeDasharray={`${(targetPercent / 100) * 352} 352`} strokeLinecap="round" />
                </svg>
                <span className="absolute text-2xl font-extrabold text-slate-900">{targetPercent}%</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm"><span className="text-slate-500">Current</span><span className="font-bold text-slate-900">{(stats.total_revenue / 100000).toFixed(1)}L</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-500">Target</span><span className="font-bold text-slate-900">{(stats.total_target / 100000).toFixed(1)}L</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-500">Growth</span>
                <span className={`font-bold flex items-center gap-1 ${stats.growth_percent >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {stats.growth_percent >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                  {stats.growth_percent}%
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Visits */}
        <Card className="lg:col-span-8 border border-border/60 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-bold text-slate-900">Recent Visits</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentVisits.map(v => (
                <div key={v.id} data-testid={`recent-visit-${v.id}`} className="flex items-center justify-between p-3 rounded-lg border border-border/40 hover:bg-slate-50/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${v.status === 'completed' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>
                      {v.status === 'completed' ? <CheckCircle2 className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{v.purpose}</p>
                      <p className="text-xs text-slate-400">{v.visit_date}</p>
                    </div>
                  </div>
                  <Badge variant={v.status === 'completed' ? 'default' : 'secondary'} className="text-xs capitalize">
                    {v.status}
                  </Badge>
                </div>
              ))}
              {recentVisits.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No visits yet</p>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KPICard({ testId, icon: Icon, label, value, sub, trend, trendLabel, color }) {
  const colorMap = {
    amber: 'bg-amber-100 text-amber-700',
    blue: 'bg-blue-100 text-blue-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    purple: 'bg-purple-100 text-purple-700',
  };

  return (
    <div data-testid={testId} className="kpi-card fade-in">
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorMap[color] || colorMap.amber}`}>
          <Icon className="w-5 h-5" strokeWidth={1.5} />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-xs font-bold ${trend >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            {trend >= 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
            {Math.abs(trend)}%
            {trendLabel && <span className="text-slate-400 font-normal ml-0.5">{trendLabel}</span>}
          </div>
        )}
      </div>
      <div className="mt-4">
        <p className="text-2xl font-extrabold text-slate-900">{value}</p>
        <p className="text-sm text-slate-500 mt-0.5">{label}</p>
      </div>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}
